import { formatEncoded, type ViewDocument, type ViewNode } from "@e308/ux";
import type { SeatObservation } from "../types.js";
import type { AppIntent, AppState } from "./app-source.js";
import { armySummary } from "./army-summary-view.js";
import { bordersTab } from "./borders-view.js";
import { botControllerStatus } from "./bot-lobby-view.js";
import { economyTab } from "./economy-view.js";
import { advisorToggle, attackWarnings, guidanceView } from "./guidance-view.js";
import { lobby } from "./lobby-view.js";
import { logTab, resultsView } from "./log-view.js";
import { researchTab } from "./research-view.js";
import { valleyTab } from "./valley-view.js";

const tabs = ["Town", "Factories", "Borders", "Research", "Valley", "Shift Log"] as const;

export function projectApp(state: AppState): ViewDocument<AppIntent, string> {
  const content: ViewNode<AppIntent, string>[] = [
    {
      kind: "section",
      id: "masthead",
      style: { className: state.observation ? "kf-masthead kf-masthead-playing" : "kf-masthead" },
      children: [
        {
          kind: "description",
          id: "eyebrow",
          content: [{ kind: "text", value: "THE VALLEY IS OPEN FOR BUSINESS" }],
        },
        { kind: "heading", id: "title", level: 1, text: "Kilnfront" },
        {
          kind: "description",
          id: "tagline",
          content: [
            { kind: "text", value: "Factories build factories. Neighbors bring siege guns." },
          ],
        },
      ],
    },
  ];
  if (state.commandFeedback)
    content.push({
      kind: "command-feedback",
      id: "practice-command-feedback",
      targetId: state.commandFeedback.targetId,
      state: state.commandFeedback.state,
      message: state.commandFeedback.message,
      presentation: "toast",
      focusOnError: false,
    });
  if (state.error) content.push(notice("error", state.error, "danger"));
  if (state.phase === "loading") content.push(notice("loading", "Loading browser host…"));
  else if (state.phase === "lobby" || state.phase === "starting" || !state.observation)
    content.push(...lobby(state));
  else content.push(...match(state));
  return { title: "Kilnfront", content };
}

function match(state: AppState): ViewNode<AppIntent, string>[] {
  const observation = state.observation as SeatObservation;
  return [
    ...resultsView(state),
    {
      kind: "section",
      id: "status-strip",
      variant: "status-strip",
      children: [
        quantity("Headquarters", observation.self.headquartersIntegrity, "hq"),
        quantity("Metal", observation.self.metal, "metal"),
        quantity("Metal rate", observation.self.economy.netMetal, "metal-rate", "/s"),
        quantity("Research Points", observation.self.researchPoints, "rp"),
        notice(
          "threats",
          threatText(observation),
          currentThreats(observation).length ? "danger" : "positive",
        ),
        notice("next-research", researchText(observation)),
        notice(
          "clock",
          `${formatClock(observation.gameTimeMs)} · game time${state.paused ? " · PAUSED" : ""}`,
        ),
        {
          kind: "section",
          id: "attack-warning-slot",
          style: { className: "kf-alert-slot" },
          children: attackWarnings(state),
        },
        armySummary(observation),
      ],
    },
    matchToolbar(state),
    ...guidanceView(state),
    {
      kind: "tabs",
      id: "main-navigation",
      tabs: tabs.map((label) => {
        if (label === "Town" || label === "Factories") return economyTab(label, state);
        if (label === "Borders") return bordersTab(state);
        if (label === "Research") return researchTab(state);
        if (label === "Valley") return valleyTab(state);
        return logTab(state);
      }),
    },
    {
      kind: "action",
      id: "restart",
      action: {
        id: "restart",
        label: "Return to practice setup",
        enabled: true,
        intent: { kind: "restart" },
        blockers: [],
      },
    },
  ];
}

function currentThreats(observation: SeatObservation): readonly string[] {
  return observation.seats.flatMap((seat) => {
    const order = seat.logistics.borders[observation.self.id]?.standingOrder;
    return order && order !== "hold" ? [`${seat.name}: ${order}`] : [];
  });
}

function threatText(observation: SeatObservation): string {
  const threats = currentThreats(observation);
  return threats.length ? `Threats — ${threats.join(", ")}` : "Threats — none";
}

function researchText(observation: SeatObservation): string {
  const active = observation.self.research.active;
  if (!active) return "Next research — none";
  const seconds = Math.max(0, Math.ceil((active.completesAtMs - observation.gameTimeMs) / 1_000));
  return `Next research — ${active.order.target} in ${seconds}s`;
}

function quantity(
  label: string,
  encoded: string,
  id: string,
  suffix = "",
): ViewNode<AppIntent, string> {
  return {
    kind: "custom",
    id,
    render(document) {
      const element = document.createElement("span");
      element.className = "metric";
      element.dataset.metric = id;
      element.title = `${label}: ${encoded}${suffix}`;
      const strong = document.createElement("strong");
      strong.textContent = label;
      const value = document.createElement("span");
      value.className = "metric-value";
      value.textContent = `${display(encoded)}${suffix}`;
      element.append(strong, value);
      return element;
    },
  };
}

function display(encoded: string): string {
  return formatEncoded(encoded, { significantDigits: 5, plainDecimalPlaces: 2 });
}

function formatClock(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1_000);
  const minutes = Math.floor(seconds / 60);
  return `Shift ${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function notice(
  id: string,
  text: string,
  tone: "neutral" | "positive" | "warning" | "danger" = "neutral",
): ViewNode<AppIntent, string> {
  return { kind: "notification", id, text, tone };
}

function matchToolbar(state: AppState): ViewNode<AppIntent, string> {
  return {
    kind: "section",
    id: "match-toolbar",
    style: { className: "kf-toolbar" },
    children: [
      advisorToggle(state),
      {
        kind: "infobox",
        id: "match-details",
        title: "Shift details & computer towns",
        initiallyOpen: false,
        content: [
          {
            kind: "description",
            id: "worker-status",
            content: [
              {
                kind: "text",
                value: state.workerOwned
                  ? "Private practice · progress stays on this device."
                  : "Preparing your practice match…",
              },
            ],
          },
          botControllerStatus(state),
        ],
      },
      {
        kind: "action",
        id: "practice-pause",
        action: {
          id: "practice-pause",
          label: state.paused ? "Resume practice" : "Pause practice",
          enabled: !state.replayActive,
          intent: { kind: "set-practice-paused", paused: !state.paused },
          blockers: state.replayActive
            ? [{ kind: "disabled", actionId: "practice-pause", reasonKey: "replay-is-read-only" }]
            : [],
        },
      },
    ],
  };
}
